import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import { can } from "@/lib/permissions";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { MagnifyingGlass, FileXls, FilePdf, TrashSimple, X } from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmDialog";
import { fmtDate } from "@/lib/dateUtils";
import { SortableTh, useSortableRows } from "@/components/SortableTh";

export default function Inventory() {
  const { user } = useAuth();
  const { activeSiteId, siteParams } = useSites();
  const askConfirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filter, setFilter] = useState({ supplier_id: "all", date_from: "", date_to: "", material_name: "" });
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const load = async () => {
    const params = { ...siteParams() };
    if (filter.supplier_id && filter.supplier_id !== "all") params.supplier_id = filter.supplier_id;
    if (filter.date_from) params.date_from = filter.date_from;
    if (filter.date_to) params.date_to = filter.date_to;
    if (filter.material_name) params.material_name = filter.material_name;
    const { data } = await axios.get(`${API}/materials`, { params });
    setRows(data);
    setSelectedIds(new Set()); // her yüklemede seçimi sıfırla
  };

  useEffect(() => { axios.get(`${API}/suppliers`).then(r => setSuppliers(r.data)); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeSiteId]);

  const totalValue = useMemo(
    () => rows.reduce((a, r) => a + (r.quantity || 0) * (r.unit_price || 0), 0),
    [rows]
  );

  const { sort, toggleSort, sortedRows } = useSortableRows(rows, {
    defaultKey: "delivery_date", defaultDir: "desc",
    getters: {
      delivery_date: (r) => r.delivery_date || "",
      site_name: (r) => r.site_name || "",
      supplier_name: (r) => r.supplier_name || "",
      material_name: (r) => r.material_name || "",
      quantity: (r) => Number(r.quantity) || 0,
      unit_price: (r) => Number(r.unit_price) || 0,
      delivery_note_no: (r) => r.delivery_note_no || "",
      created_by_name: (r) => r.created_by_name || "",
    },
  });

  const exportUrl = (kind) => {
    const p = new URLSearchParams(siteParams());
    if (filter.supplier_id && filter.supplier_id !== "all") p.set("supplier_id", filter.supplier_id);
    if (filter.date_from) p.set("date_from", filter.date_from);
    if (filter.date_to) p.set("date_to", filter.date_to);
    if (kind === "pdf") p.set("report_kind", "stock");
    return `${API}/export/materials.${kind}?${p.toString()}`;
  };

  const del = async (id) => {
    const ok = await askConfirm({
      title: "Kaydı Sil",
      description: "Bu malzeme kaydını silmek istiyor musunuz? Bu işlem geri alınamaz.",
      destructive: true,
      confirmText: "Sil",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/materials/${id}`);
      toast.success("Silindi");
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const isAdmin = can.delete(user);

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selectedIds.size === rows.length && rows.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rows.map(r => r.id)));
    }
  };
  const clearSelection = () => setSelectedIds(new Set());

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const ok = await askConfirm({
      title: "Toplu Silme",
      description: `${selectedIds.size} adet malzeme kaydını silmek istiyor musunuz? 10 saniye içinde geri alabilirsiniz.`,
      destructive: true,
      confirmText: `Evet, ${selectedIds.size} Kaydı Sil`,
    });
    if (!ok) return;
    setBulkDeleting(true);
    try {
      const { data } = await axios.post(`${API}/materials/bulk-delete`, {
        ids: Array.from(selectedIds),
      });
      const deletedIds = data.ids || Array.from(selectedIds);
      await load();
      toast.success(`${data.deleted} kayıt silindi`, {
        duration: 10000,
        action: {
          label: "Geri Al",
          onClick: async () => {
            try {
              const res = await axios.post(`${API}/materials/undo-delete`, { ids: deletedIds });
              toast.success(`${res.data.restored} kayıt geri alındı`);
              await load();
            } catch (err) { toast.error(formatErr(err)); }
          },
        },
      });
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBulkDeleting(false);
    }
  };

  const allSelected = rows.length > 0 && selectedIds.size === rows.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < rows.length;

  return (
    <Shell
      title="Stok Listesi"
      subtitle="Tüm Malzemeler"
      actions={
        <>
          <a href={exportUrl("xlsx")} data-testid="export-xlsx">
            <Button variant="outline" className="rounded-none sm:rounded-sm border-slate-300">
              <FileXls size={16} className="mr-2" /> Excel
            </Button>
          </a>
          <a href={exportUrl("pdf")} data-testid="export-pdf">
            <Button className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
              <FilePdf size={16} className="mr-2" /> PDF
            </Button>
          </a>
        </>
      }
    >
      <Card className="p-6 rounded-none sm:rounded-sm border-slate-200 shadow-sm mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Malzeme Ara</Label>
            <div className="relative mt-2">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="filter-material"
                value={filter.material_name}
                onChange={(e) => setFilter(f => ({ ...f, material_name: e.target.value }))}
                placeholder="Malzeme adı..."
                className="pl-9 rounded-none sm:rounded-sm"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Firma</Label>
            <Select value={filter.supplier_id} onValueChange={(v) => setFilter(f => ({ ...f, supplier_id: v }))}>
              <SelectTrigger className="mt-2 rounded-none sm:rounded-sm" data-testid="filter-supplier">
                <SelectValue placeholder="Tümü" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Tarih (Başlangıç)</Label>
            <Input type="date" className="mt-2 rounded-none sm:rounded-sm" data-testid="filter-date-from"
                   value={filter.date_from} onChange={(e) => setFilter(f => ({ ...f, date_from: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Tarih (Bitiş)</Label>
            <Input type="date" className="mt-2 rounded-none sm:rounded-sm" data-testid="filter-date-to"
                   value={filter.date_to} onChange={(e) => setFilter(f => ({ ...f, date_to: e.target.value }))} />
          </div>
          <div className="flex items-end">
            <Button onClick={load} data-testid="apply-filter"
                    className="w-full h-10 rounded-none sm:rounded-sm bg-[#95C11F] hover:bg-[#7FA518] text-[#1B2A3A] font-bold">
              Uygula
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-0 rounded-none sm:rounded-sm border-slate-200 shadow-sm overflow-hidden" data-testid="inventory-table">
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="text-sm text-slate-600">
            <span className="font-bold text-slate-900">{rows.length}</span> kayıt bulundu
          </div>
          <div className="text-sm text-slate-600">
            Toplam Değer:{" "}
            <span className="font-mono font-bold text-slate-900">₺{totalValue.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
        </div>

        {/* Toplu seçim bar — seçim varsa gösterilir */}
        {isAdmin && selectedIds.size > 0 && (
          <div
            data-testid="bulk-selection-bar"
            className="flex flex-wrap items-center justify-between gap-3 px-6 py-3 border-b border-red-200 bg-red-50"
          >
            <div className="text-sm text-red-900 font-medium">
              <span className="font-mono font-bold" data-testid="bulk-selection-count">{selectedIds.size}</span> kayıt seçildi
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                onClick={clearSelection}
                data-testid="bulk-clear-btn"
                className="h-8 rounded-none sm:rounded-sm border-slate-300 text-slate-700 hover:bg-slate-100 text-xs"
              >
                <X size={14} className="mr-1" /> Seçimi Temizle
              </Button>
              <Button
                onClick={bulkDelete}
                disabled={bulkDeleting}
                data-testid="bulk-delete-btn"
                className="h-8 rounded-none sm:rounded-sm bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-widest"
              >
                <TrashSimple size={14} className="mr-1.5" />
                {bulkDeleting ? "Siliniyor..." : `Seçilenleri Sil (${selectedIds.size})`}
              </Button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-200 bg-white">
                {isAdmin && (
                  <th className="w-10 px-4 py-3">
                    <Checkbox
                      checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
                      onCheckedChange={toggleAll}
                      data-testid="bulk-select-all"
                      aria-label="Tümünü seç"
                    />
                  </th>
                )}
                <SortableTh label="Tarih" k="delivery_date" sort={sort} onSort={toggleSort} className="text-left px-6 py-3" />
                <SortableTh label="Şantiye" k="site_name" sort={sort} onSort={toggleSort} className="text-left px-6 py-3" />
                <SortableTh label="Firma" k="supplier_name" sort={sort} onSort={toggleSort} className="text-left px-6 py-3" />
                <SortableTh label="Malzeme" k="material_name" sort={sort} onSort={toggleSort} className="text-left px-6 py-3" />
                <SortableTh label="Miktar" k="quantity" sort={sort} onSort={toggleSort} className="text-right px-6 py-3" align="right" />
                <SortableTh label="Birim Fiyat" k="unit_price" sort={sort} onSort={toggleSort} className="text-right px-6 py-3" align="right" />
                <SortableTh label="İrsaliye" k="delivery_note_no" sort={sort} onSort={toggleSort} className="text-left px-6 py-3" />
                <SortableTh label="Personel" k="created_by_name" sort={sort} onSort={toggleSort} className="text-left px-6 py-3" />
                {isAdmin && <th className="px-6 py-3"></th>}
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((r, i) => {
                const isChecked = selectedIds.has(r.id);
                return (
                  <tr
                    key={r.id}
                    className={`${isChecked ? "bg-red-50" : (i % 2 ? "bg-slate-50" : "bg-white")} hover:bg-slate-100 transition-colors`}
                    data-testid={`inv-row-${i}`}
                  >
                    {isAdmin && (
                      <td className="w-10 px-4 py-3">
                        <Checkbox
                          checked={isChecked}
                          onCheckedChange={() => toggleOne(r.id)}
                          data-testid={`bulk-select-${r.id}`}
                          aria-label={`Seç: ${r.material_name}`}
                        />
                      </td>
                    )}
                    <td className="px-6 py-3 font-mono text-xs">{fmtDate(r.delivery_date)}</td>
                    <td className="px-6 py-3 text-slate-700 font-medium text-xs">{r.site_name || "-"}</td>
                    <td className="px-6 py-3 font-medium">{r.supplier_name}</td>
                    <td className="px-6 py-3">{r.material_name}</td>
                    <td className="px-6 py-3 text-right font-mono">{r.quantity} {r.unit}</td>
                    <td className="px-6 py-3 text-right font-mono">{r.unit_price ? `₺${r.unit_price}` : "-"}</td>
                    <td className="px-6 py-3 text-slate-600 font-mono text-xs">{r.delivery_note_no || "-"}</td>
                    <td className="px-6 py-3 text-slate-600">{r.created_by_name}</td>
                    {isAdmin && (
                      <td className="px-6 py-3 text-right">
                        <button data-testid={`del-${r.id}`} onClick={() => del(r.id)}
                                className="text-red-600 hover:text-red-800">
                          <TrashSimple size={16} />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {rows.length === 0 && (
                <tr><td colSpan={isAdmin ? 10 : 8} className="py-12 text-center text-slate-500">Kayıt bulunamadı.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </Shell>
  );
}
